import React, { useCallback } from 'react';
import { VirtualizedLogList } from '../VirtualizedLogList';
import { VIRTUALIZED_LIST } from '../../constants';

type LogListProps<T> = {
  items: T[];
  renderItem: (item: T, index: number) => JSX.Element;
  loading?: boolean;
  totalCount?: number;
  onLoadMore: () => void | Promise<void>;
  hasMore: boolean;
  followOutput?: (isAtBottom: boolean) => boolean | 'smooth';
  onAtBottomStateChange?: (isAtBottom: boolean) => void;
  showInitialLoader?: boolean;
  components?: {
    Header?: React.ComponentType;
    Footer?: React.ComponentType;
  };
};

export const LogList = <T,>({
  items,
  renderItem,
  loading = false,
  totalCount,
  onLoadMore,
  hasMore,
  onAtBottomStateChange,
  components,
  showInitialLoader,
}: LogListProps<T>) => {
  const handleLoadMore = useCallback(async () => {
    if (!hasMore) return;
    // Prevent endReached from firing during initial load or after filter changes
    if (showInitialLoader) return;
    if (loading) return;
    if (totalCount && totalCount < VIRTUALIZED_LIST.MIN_ITEMS_FOR_LOAD_MORE) return;

    await onLoadMore();
  }, [hasMore, onLoadMore, showInitialLoader, loading, totalCount]);

  return (
    <VirtualizedLogList
      items={items}
      renderItem={renderItem}
      atTopThreshold={100}
      initialTopMostItemIndex={0}
      loading={loading}
      totalCount={totalCount}
      onEndReached={hasMore ? handleLoadMore : undefined}
      followOutput={false}
      onAtBottomStateChange={onAtBottomStateChange}
      components={components}
    />
  );
};
